// src/components/auth/VerifyEmail.js
import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import WelcomeHeader from '../layout/WelcomeHeader';
import styles from './Login.module.css';
import { authApi } from '../../api';

function VerifyEmail() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [status, setStatus] = useState(token ? 'verifying' : 'missing_token'); // verifying | success | error | missing_token
  const [resendEmail, setResendEmail] = useState('');
  const [resendStatus, setResendStatus] = useState(null);
  const ranRef = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!token || ranRef.current) return;
    ranRef.current = true;

    const verify = async () => {
      const result = await authApi.verifyEmail(token);
      setStatus(result.success ? 'success' : 'error');
    };
    verify();
  }, [token]);

  const handleResendVerification = async (e) => {
    e.preventDefault();
    if (!resendEmail.trim()) return;
    setResendStatus('sending');
    const result = await authApi.resendVerification(resendEmail.trim());
    if (result.success) {
      setResendStatus('sent');
    } else if (result.error?.code === 'RESEND_COOLDOWN') {
      setResendStatus('cooldown');
    } else {
      setResendStatus('error');
    }
  };

  return (
    <>
      <WelcomeHeader />
      <div className={styles.authContainer}>
        <div className={styles.authCard}>
          <div className={styles.authLogo}>
            <img src={require('../../assets/logo_1.png')} alt="MangaShelf" className={styles.authLogoImg} />
          </div>

          <div className={styles.authHeader}>
            <h1 className={styles.authTitle}>{t('verifyEmail.title')}</h1>
          </div>

          {status === 'verifying' && (
            <p className={styles.authSubtitle} style={{ textAlign: 'center' }}>
              {t('verifyEmail.verifying')}
            </p>
          )}

          {status === 'success' && (
            <div className={styles.authActions}>
              <p className={styles.authSubtitle}>
                ✅ {t('verifyEmail.success')}
              </p>
              <button onClick={() => navigate('/login')} className={styles.submitButton}>
                {t('resetPassword.goToLogin')}
              </button>
            </div>
          )}

          {(status === 'error' || status === 'missing_token') && (
            <div className={styles.formGroup}>
              <div className={styles.errorMessage}>
                {status === 'missing_token'
                  ? t('verifyEmail.noTokenInUrl')
                  : t('verifyEmail.invalidLink')}
              </div>

              {resendStatus === 'sent' ? (
                <p className={styles.authSubtitle}>{t('auth.verificationResent')}</p>
              ) : (
                <>
                  <label className={styles.formLabel}>{t('auth.resendEmailLabel')}</label>
                  <input
                    type="email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    className={styles.formInput}
                    placeholder={t('auth.resendEmailPlaceholder')}
                  />
                  <button
                    type="button"
                    className={styles.linkButton}
                    onClick={handleResendVerification}
                    disabled={resendStatus === 'sending' || !resendEmail.trim()}
                  >
                    {resendStatus === 'sending' ? t('auth.sending') : t('auth.resendVerification')}
                  </button>
                  {resendStatus === 'cooldown' && (
                    <p className={styles.authSubtitle}>{t('auth.resendCooldown')}</p>
                  )}
                  {resendStatus === 'error' && (
                    <p className={styles.authSubtitle}>{t('auth.resendError')}</p>
                  )}
                </>
              )}
            </div>
          )}

          <div className={styles.divider}>
            <span>{t('auth.or')}</span>
          </div>

          <div className={styles.authActions}>
            <button onClick={() => navigate('/login')} className={styles.backButton}>
              ← {t('forgotPassword.backToLogin')}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

export default VerifyEmail;
